import request from "superagent";

import AuthorizeService from "../components/api-authorization/AuthorizeService";

export default {
	async get(url)
	{
		const response = await request
			.get(url)
			.set("Authorization", "Bearer " + await AuthorizeService.getAccessToken());

		// TODO: what if we don't want json?
		return JSON.parse(response.text);
	},

	async post(url, data, options)
	{
		// Handle case where options is empty
		options = options || {};
		// Handle default options
		options = { ...{ responseExpected: false, returnResponse: false }, ...options };
		const response = await request
			.post(url)
			.set("Authorization", "Bearer " + await AuthorizeService.getAccessToken())
			.send(data);

		if (options.returnResponse)
		{
			return response;
		}

		if (!response.body && options.responseExpected)
		{
			throw new Error("No response was received.");
		}

		return response.body;
	},

	async put(url, data, options)
	{
		options = options || { responseExpected: false };
		const response = await request
			.put(url)
			.set("Authorization", "Bearer " + await AuthorizeService.getAccessToken())
			.send(data);

		if (!response.body && options.responseExpected)
		{
			throw new Error("No response was received.");
		}

		return response.body;
	},

	async delete(url, data)
	{
		let response = null;
		if (data)
		{
			response = await request
				.delete(url)
				.set("Authorization", "Bearer " + await AuthorizeService.getAccessToken())
				.set("Content-Type", "application/json")
				.send(data);
		}
		else
		{
			response = await request
				.delete(url)
				.set("Authorization", "Bearer " + await AuthorizeService.getAccessToken())
				.set("Content-Type", "application/json");
		}

		return response.body;
	},

	async addOrUpdate(url, data, options)
	{
		options = options || { responseExpected: false };
		return data.id
			? await this.put(`${url}/${data.id}`, data, options)
			: await this.post(url, data, options);
	},

	async getAccounts()
	{
		return await this.get("/api/accounts");
	},

	async getIsCurrencyChangeAllowed(accountId) {
		return await this.get(`/api/accounts/${accountId}/iscurrencychangeallowed`);
	},

	async saveAccount(account)
	{
		return await this.addOrUpdate("/api/accounts", account);
	},

	async saveCategory(category)
	{
		return await this.addOrUpdate("/api/categories", category);
	},

	async saveAccountLineItem(account, lineItem)
	{
		return lineItem.id
			? await this.put(`/api/accounts/${account.id}/transactions/${lineItem.transactionId}`, lineItem)
			: await this.post(`/api/accounts/${account.id}/transactions`, lineItem);
	},

	async deleteLineItem(lineItem)
	{
		if (lineItem.id && lineItem.transactionId && lineItem.accountId)
		{
			await this.delete(`/api/accounts/${lineItem.accountId}/transactions/${lineItem.transactionId}/lineitems/${lineItem.id}`, lineItem);
		}
		else
		{
			throw new Error("Line Item is missing id, transactionId, or accountId.");
		}
	},

	async getAccountLineItems(accountId, pageIndex, sort, search)
	{
		let url = `/api/accounts/${accountId}/transactions`;
		const urlParameters = [];
		if (pageIndex > 0)
		{
			urlParameters.push(`page=${pageIndex}`);
		}
		if (sort && sort.length > 0)
		{
			urlParameters.push(`ordering=${sort.join("|")}`);
		}
		else
		{
			urlParameters.push("ordering=-date");
		}
		if (search)
		{
			urlParameters.push(`search=${search}`);
		}
		if (urlParameters.length > 0)
		{
			url += `?${urlParameters.join("&")}`;
		}

		return await this.get(url);
	},

	async getRecentDescriptions(accountId, value)
	{
		return await this.get(`/api/accounts/${accountId}/transactions/recent?description=${encodeURIComponent(value)}`);
	},

	async getCategories()
	{
		return await this.get("/api/categories");
	},

	async categoryIsInUse(categoryId)
	{
		return await this.get(`/api/categories/${categoryId}/isused`);
	},

	async deleteCategory(categoryId, replacementCategoryId)
	{
		let data = null;
		if (replacementCategoryId)
		{
			data = { replacementCategoryId };
		}

		return await this.delete(`/api/categories/${categoryId}`, data);
	},

	async getTransaction(transactionId)
	{
		return await this.get(`/api/transactions/${transactionId}`);
	},

	async saveTransaction(transaction)
	{
		return await this.addOrUpdate("/api/transactions", transaction, {responseExpected: true});
	},

	async uploadFile(file)
	{
		const response = await request
			.post("/api/imports")
			.set("Authorization", "Bearer " + await AuthorizeService.getAccessToken())
			.attach("file", file);

		return response.body;
	},

	async getColumns(fileId)
	{
		return await this.get(`/api/imports/${fileId}`);
	},

	async getBudgetAllocations()
	{
		return await this.get("/api/budgetallocations");
	},

	async getBudgetTemplates()
	{
		return await this.get("/api/budgettemplates");
	},

	async getBudgetTemplate(id)
	{
		return await this.get(`/api/budgettemplates/${id}`);
	},

	async budgetTransfer(transferDetails)
	{
		return await this.post("/api/budgetallocations/transfer", transferDetails);
	},

	async upgradeToPremium(token)
	{
		return await this.post("/api/userprofile/upgrade", token, {returnResponse: true});
	},

	async setBudgetAllocationOrder(categoryId, sequence)
	{
		return await this.addOrUpdate(`/api/budgetallocations/${categoryId}/setsequence`, { sequence });
	},

	async saveBudgetTemplate(budgetTemplate)
	{
		return await this.addOrUpdate("/api/budgettemplates", budgetTemplate, {responseExpected: true});
	},

	deleteBudgetTemplate(id)
	{
		return new Promise((resolve, reject) => {
			this.delete(`/api/budgettemplates/${id}`)
				.end(err => {
					if (err)
					{
						reject(err);
					}
					resolve(id);
				});
		});
	},

	async runBudgetTemplate(id)
	{
		return await this.post(`/api/budgettemplates/${id}/run`);
	},

	async getUserProfile()
	{
		return await this.get("/api/userprofile");
	},

	async updateUserProfile(property, value)
	{
		return await this.put("/api/userprofile", { property, value });
	},

	async getBudgetAuditInformation(categoryId, currencyId, pageIndex)
	{
		let url = `/api/budgetaudit/${categoryId}`;
		const urlParameters = [];
		if (pageIndex > 0)
		{
			urlParameters.push(`page=${pageIndex}`);
		}
		if (!!currencyId)
		{
			urlParameters.push(`currencyId=${currencyId}`);
		}
		if (urlParameters.length > 0)
		{
			url += `?${urlParameters.join("&")}`;
		}
		return await this.get(url);
	},

	async getTags()
	{
		return await this.get("/api/tags");
	},

	async saveTag(tag)
	{
		return await this.addOrUpdate("/api/tags", tag);
	},

	async deleteTag(tagId)
	{
		return await this.delete(`/api/tags/${tagId}`);
	},

	async getCurrencies()
	{
		const currencies = await this.get("/api/currencies");
		return currencies.map(currency => {
			if (currency && currency.format && typeof currency.format === "string")
			{
				try
				{
					currency.format = JSON.parse(currency.format);
				}
				catch (e)
				{
					// Ignore this, it was probably just a string
				}
			}
			return currency;
		});
	},

	async saveCurrency(currency) {
		currency = await this.addOrUpdate("/api/currencies", currency);
		if (currency && currency.format && typeof currency.format === "string")
		{
			try
			{
				currency.format = JSON.parse(currency.format);
			}
			catch (e)
			{
				// Ignore this, it was probably just a string
			}
		}

		return currency;
	},

	async deleteCurrency(currencyId) {
		return await this.delete(`/api/currencies/${currencyId}`);
	},

	async getCurrencyInUse(currencyId) {
		return await this.get(`/api/currencies/${currencyId}/inuse`);
	},

	async getCurrencyDefinitions()
	{
		return await this.get("/api/currencydefinitions");
	},

	async getSubscriptionInformation()
	{
		return await this.get("/api/userprofile/subscriptioninformation");
	},

	async cancelSubscription(customerId, subscriptionId)
	{
		return await this.delete(`/api/userprofile/subscription/${customerId}/${subscriptionId}`);
	},

	async reactivateSubscription(customerId, subscriptionId)
	{
		return await this.post(`/api/userprofile/subscription/${customerId}/${subscriptionId}/reactivate`);
	},

	async sendContactUs(name, message)
	{
		return await this.post("/api/features/contactus", { name, message });
	},
};



// WEBPACK FOOTER //
// ./Components/utils/Api.jsx